import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Copy, Check, Plug } from "lucide-react";
import { Header } from "@/client/components/layout/header";
import { Button } from "@/client/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/client/components/ui/tabs";
import { useSession } from "@/client/hooks/use-session";
import { apiJson } from "@/client/lib/api";
import { toast } from "sonner";

export const Route = createFileRoute("/agents/")({
  component: ConnectedAgents,
});
interface AgentStory {
  id: string;
  name: string;
  automaticAiChecks: boolean;
  ownerUserId: string;
}
interface Connection {
  id: string;
  clientName: string;
  clientId: string;
  canPropose: boolean;
  revokedAt: string | null;
  lastUsedAt: string | null;
  stories: AgentStory[];
}
function ConnectedAgents() {
  const { user } = useSession();
  const [data, setData] = useState<{
    endpoint: string;
    connections: Connection[];
  } | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await apiJson("/api/agents"));
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load connections");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);
  async function disconnect(id: string) {
    setBusy(id);
    try {
      await apiJson(`/api/agents/${id}`, { method: "DELETE" });
      await load();
      toast.success("Agent disconnected. Pending suggestions were cancelled.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Disconnect failed");
    } finally {
      setBusy(null);
    }
  }
  async function setChecks(story: AgentStory, enabled: boolean) {
    setBusy(story.id);
    try {
      await apiJson(`/api/agents/stories/${story.id}/checks`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not change checks");
    } finally {
      setBusy(null);
    }
  }
  const active = data?.connections.filter((c) => !c.revokedAt) ?? [];
  const stories = [
    ...new Map(active.flatMap((c) => c.stories).map((s) => [s.id, s])).values(),
  ];
  return (
    <div className="flex h-full flex-col">
      <Header />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-8 px-4 py-8 sm:px-6">
          <div>
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← Projects
            </Link>
            <h1 className="mt-4 text-2xl font-semibold">Connected agents</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Use your preferred agent to read your stories and suggest scene
              edits. Review every suggestion in Renteller.
            </p>
          </div>
          {error && (
            <div role="alert" className="space-y-2 text-sm text-destructive">
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={() => void load()}>
                Try again
              </Button>
            </div>
          )}
          {!data && !error && (
            <p role="status" className="text-sm text-muted-foreground">
              Loading connections…
            </p>
          )}
          {data && (
            <>
              <section className="space-y-4">
                <h2 className="font-medium">Connect your agent</h2>
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2">
                  <code className="min-w-0 flex-1 break-all px-1 text-sm">
                    {data.endpoint}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Copy MCP URL"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(data.endpoint)
                        .then(() => setCopied(true))
                        .catch(() =>
                          toast.error(
                            "Couldn't copy. Select and copy the URL.",
                          ),
                        )
                    }
                  >
                    {copied ? <Check /> : <Copy />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <Tabs defaultValue="chat">
                  <TabsList>
                    <TabsTrigger value="chat">Chat apps</TabsTrigger>
                    <TabsTrigger value="code">Coding agents</TabsTrigger>
                  </TabsList>
                  <TabsContent value="chat" className="space-y-3 text-sm">
                    <p>
                      Add the URL as a remote MCP connection in your agent's
                      settings. Open its authorization link, sign in to
                      Renteller, and select your stories and permissions.
                    </p>
                    <p className="text-muted-foreground">
                      In Claude, look for Connectors. In ChatGPT, look for
                      custom apps or MCP settings. Availability depends on your
                      plan and workspace settings.
                    </p>
                  </TabsContent>
                  <TabsContent value="code" className="space-y-3 text-sm">
                    <p>
                      Add a remote HTTP server, then complete the agent's
                      browser authorization.
                    </p>
                    <div className="space-y-2 overflow-x-auto rounded-md border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">Codex</p>
                      <code className="block whitespace-pre">
                        codex mcp add renteller --url {data.endpoint}
                      </code>
                      <code className="block whitespace-pre">
                        codex mcp login renteller
                      </code>
                      <p className="text-xs text-muted-foreground">
                        Claude Code
                      </p>
                      <code className="block whitespace-pre">
                        claude mcp add --transport http renteller{" "}
                        {data.endpoint}
                      </code>
                    </div>
                    <p className="text-muted-foreground">
                      In Claude Code, run /mcp to authorize the connection.
                      Renteller does not need your agent provider's API key.
                    </p>
                  </TabsContent>
                </Tabs>
                <p className="text-sm text-muted-foreground">
                  Your agent handles model billing. Reading, validating and
                  submitting suggestions use no Renteller model credits.
                </p>
              </section>
              <section className="space-y-3">
                <h2 className="font-medium">Your connections</h2>
                {!active.length ? (
                  <div className="rounded-md border border-dashed p-5 text-sm text-muted-foreground">
                    <Plug className="mb-2 size-5" />
                    No agents connected yet. Add the URL above in your agent to
                    begin.
                  </div>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {active.map((c) => (
                      <li key={c.id} className="space-y-2 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="break-words font-medium">
                              {c.clientName}
                            </h3>
                            <p className="mt-1 break-all text-xs text-muted-foreground">
                              {c.clientId}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy === c.id}
                            onClick={() => void disconnect(c.id)}
                          >
                            {busy === c.id ? "Disconnecting…" : "Disconnect"}
                          </Button>
                        </div>
                        <p className="text-sm">
                          {c.canPropose
                            ? "Read stories and submit suggestions"
                            : "Read stories"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {c.stories.map((s) => s.name).join(", ") ||
                            "No selected stories are currently accessible"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {c.lastUsedAt
                            ? `Last used ${new Date(c.lastUsedAt).toLocaleString()}`
                            : "Not used yet"}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {stories.length > 0 && (
                <section className="space-y-3">
                  <h2 className="font-medium">Automatic Renteller checks</h2>
                  <p className="text-sm text-muted-foreground">
                    Automatic checks run Renteller's model when a story opens or
                    changes, including after an agent suggestion is applied.
                    Story owners can turn them off. Manual AI actions still use
                    Renteller model credits.
                  </p>
                  <ul className="divide-y rounded-md border">
                    {stories.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-4 p-4 text-sm"
                      >
                        <span className="min-w-0 break-words">{s.name}</span>
                        <label className="flex shrink-0 items-center gap-2">
                          <input
                            type="checkbox"
                            className="accent-primary"
                            aria-label={`Automatic checks for ${s.name}`}
                            checked={s.automaticAiChecks}
                            disabled={
                              s.ownerUserId !== user?.id || busy === s.id
                            }
                            onChange={(e) =>
                              void setChecks(s, e.target.checked)
                            }
                          />
                          {s.automaticAiChecks ? "On" : "Off"}
                        </label>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
